import { useWindowSize } from '@vueuse/core'
import { debounce } from 'lodash-es'
import type { WatchSource } from 'vue'

type ResizeBreakpoint = 'mobile' | 'tablet' | 'desktop'

const BREAKPOINTS_MAP: Record<ResizeBreakpoint, number> = {
  mobile: 0,
  tablet: 768,
  desktop: 1024
}

type Options = {
  watchSource?: WatchSource | WatchSource[]
  resizeFrom?: ResizeBreakpoint | number
}

export default function useElementsSameHeight({
  watchSource = [],
  resizeFrom = 'desktop'
}: Options = {}) {
  const elements = ref<HTMLElement[]>([])
  const size = useWindowSize()

  const calculateElementSize = (element: HTMLElement) => {
    const prevMinHeight = element.style.minHeight
    element.style.minHeight = 'auto'
    const height = element.offsetHeight
    element.style.minHeight = prevMinHeight

    return height
  }

  const onResize = (width: number) => {
    const highestElementHeight = Math.max(
      ...elements.value.map(calculateElementSize)
    )

    const breakpointWidth =
      typeof resizeFrom === 'number' ? resizeFrom : BREAKPOINTS_MAP[resizeFrom]

    const shouldResize = width >= breakpointWidth

    elements.value.forEach(element => {
      if (!shouldResize) return (element.style.minHeight = 'auto')
      if (element.clientHeight === highestElementHeight) return

      element.style.minHeight = `${highestElementHeight}px`
    })
  }

  watch(
    [size.width, ...ensureArray(watchSource)],
    debounce(([width]) => onResize(width), 50),
    {
      immediate: true
    }
  )

  return elements
}
